import {
  type ButtonHTMLAttributes,
  type ComponentType,
  forwardRef
} from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '@elfsight/lib-icon';

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: ComponentType;
  color: string;
  colorHover?: string;
  backgroundColor?: string;
  backgroundHoverColor?: string;
  overlayColor?: string;
  overlayHoverColor?: string;
  borderColor?: string;
  borderHoverColor?: string;
  size?: number;
  iconSize?: number;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      color,
      colorHover,
      size = 32,
      iconSize,
      backgroundColor,
      backgroundHoverColor,
      overlayColor,
      overlayHoverColor,
      borderColor,
      borderHoverColor,
      ...props
    },
    forwardingRef
  ) => {
    return (
      <Button
        {...props}
        ref={forwardingRef}
        _size={size}
        _color={color}
        _colorHover={colorHover}
        _backgroundColor={backgroundColor}
        _backgroundHoverColor={backgroundHoverColor}
        _borderColor={borderColor}
        _borderHoverColor={borderHoverColor}
        _overlayColor={overlayColor}
        _overlayHoverColor={overlayHoverColor}
      >
        <Icon size={iconSize || size / 2} component={icon} />
      </Button>
    );
  }
);

const Button = styled.button<{
  _backgroundColor?: string;
  _backgroundHoverColor?: string;
  _borderColor?: string;
  _borderHoverColor?: string;
  _overlayColor?: string;
  _overlayHoverColor?: string;
  _color: string;
  _colorHover?: string;
  _size: number;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition-property: border-color, background-color, transform;
  transition-duration: 0.4s;

  img,
  svg {
    transition: fill 0.4s;
  }

  &:active {
    transform: scale(0.95);
  }

  ${({ _size }) => css`
    width: ${_size}px;
    height: ${_size}px;
    border-radius: ${_size / 2}px;
    &::after {
      border-radius: ${_size / 2}px;
    }
  `}

  ${({ _color, _colorHover }) => css`
    img,
    svg {
      fill: ${_color};
    }
    &:hover img,
    &:hover svg {
      fill: ${_colorHover};
    }
  `}

  ${({ _backgroundColor, _backgroundHoverColor }) => css`
    background-color: ${_backgroundColor || 'transparent'};
    &:hover {
      background-color: ${_backgroundHoverColor};
    }
  `}

  ${({ _overlayColor, _overlayHoverColor }) => css`
    &::after {
      pointer-events: none;
      position: absolute;
      content: '';
      inset: 0;
      transition: background-color 0.2s;
      background-color: ${_overlayColor || 'transparent'};
    }
    &:hover::after {
      background-color: ${_overlayHoverColor};
    }
  `}

  ${({ _borderColor, _borderHoverColor }) => css`
    border-width: ${_borderColor ? 1 : 0}px;
    border-style: solid;
    border-color: ${_borderColor || 'transparent'};
    &:hover {
      border-color: ${_borderHoverColor};
    }
  `}

  cursor: ${({ disabled, onClick }) => {
    if (disabled) return 'not-allowed';
    if (!onClick) return 'default';
    return 'pointer';
  }};

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
    `}

  @media (prefers-reduced-motion: reduce) {
    & {
      transition: none;
    }

    & img,
    svg {
      transition: none;
    }

    &::after {
      transition: none;
    }
  }
`;
